import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Trade } from "../../../../../models";
import { autoFontColor } from "../../../../../utils/color";
import { useDropZone } from "./useDropZone";
import TradeBucket from "./TradeBucket/TradeBucket";
import ColorPicker from "./ColorPicker/ColorPicker";
import "./TradeBuckets.css";

/** One work-code group inside a trade bucket, scoped to ONE section (a code
 *  reused across sections yields one entry per section it's assigned in).
 *  `id` is the opaque drag/group key; `code`/`label` are display. */
export interface BucketCode {
  id: string;
  code: string;
  label: string;
  section: string;
}

interface Props {
  /** The staged-effective trade list (server + draft CRUD overlay). */
  trades: Trade[];
  /** Staged-effective assignment: trade id → its codes. */
  codesByTrade: Map<number, BucketCode[]>;
  unassignedCount: number;
  onAssign: (ids: string[], tradeId: number) => void;
  onUnassign: (ids: string[]) => void;
  /** Remove a code from one section's trade (✕ on a chip). */
  onRemoveFromSection: (id: string, section: string) => void;
  /** Trade CRUD — staged drafts like every other edit; nothing hits the API
   *  until the save commits. */
  onAddTrade: (name: string, fill: string, font: string) => void;
  onEditTrade: (
    id: number,
    updates: Pick<Trade, "name" | "fill_color" | "font_color">,
  ) => void;
  onDeleteTrade: (id: number) => void;
}

/** The Work Codes tab's right panel: tenant trades as drop buckets. Drag code
 *  rows (or bucket chips) onto a trade to stage the assignment; drop on
 *  "No trade" to stage an unassign. Add/edit/delete trades inline (staged). */
const TradeBuckets: React.FC<Props> = ({
  trades,
  codesByTrade,
  unassignedCount,
  onAssign,
  onUnassign,
  onRemoveFromSection,
  onAddTrade,
  onEditTrade,
  onDeleteTrade,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const addFormRef = useRef<HTMLDivElement>(null);

  const unassignZone = useDropZone(onUnassign);

  /** Colors already on other trades — dimmed in the picker. */
  const usedColors = useMemo(() => {
    const set = new Set<string>();
    for (const t of trades) {
      if (t.id !== editingId) set.add(t.fill_color.toLowerCase());
    }
    return set;
  }, [trades, editingId]);

  const scrollToAddForm = useCallback(() => {
    requestAnimationFrame(() => {
      addFormRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    });
  }, []);

  useEffect(() => {
    if (adding) {
      nameInputRef.current?.focus();
      scrollToAddForm();
    }
  }, [adding, scrollToAddForm]);

  useEffect(() => {
    if (editingId != null) editInputRef.current?.focus();
  }, [editingId]);

  const toggleExpand = useCallback((id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /** Trade names are unique per tenant (registry constraint) — catch the
   *  collision here so the save can't fail on it later. */
  const nameTaken = useCallback(
    (name: string, exceptId: number | null) =>
      trades.some(
        (t) => t.id !== exceptId && t.name.trim().toLowerCase() === name.toLowerCase(),
      ),
    [trades],
  );

  const handleAdd = useCallback(() => {
    const trimmed = newName.trim();
    if (!trimmed) {
      setFormError("Must supply a trade name");
      scrollToAddForm();
      return;
    }
    if (!newColor) {
      setFormError("Must choose a color");
      scrollToAddForm();
      return;
    }
    if (nameTaken(trimmed, null)) {
      setFormError("A trade with that name already exists");
      scrollToAddForm();
      return;
    }
    setFormError(null);
    onAddTrade(trimmed, newColor, autoFontColor(newColor));
    setNewName("");
    setNewColor("");
    setAdding(false);
  }, [newName, newColor, nameTaken, onAddTrade, scrollToAddForm]);

  const startEdit = (trade: Trade) => {
    setEditingId(trade.id);
    setEditName(trade.name);
    setEditColor(trade.fill_color);
    setFormError(null);
  };

  const handleEditSave = useCallback(() => {
    if (editingId == null) return;
    const trimmed = editName.trim();
    if (!trimmed) return;
    if (nameTaken(trimmed, editingId)) {
      setFormError("A trade with that name already exists");
      return;
    }
    onEditTrade(editingId, {
      name: trimmed,
      fill_color: editColor,
      font_color: autoFontColor(editColor),
    });
    setEditingId(null);
  }, [editingId, editName, editColor, nameTaken, onEditTrade]);

  const onFormKeyDown =
    (save: () => void, cancel: () => void) => (e: React.KeyboardEvent) => {
      // The editor's global Escape handler closes the whole modal — keep
      // form-level Escape local to the form.
      if (e.key === "Enter") save();
      if (e.key === "Escape") {
        e.stopPropagation();
        cancel();
      }
    };

  const editForm = (
    onSave: () => void,
    onCancel: () => void,
    nameVal: string,
    setName: (v: string) => void,
    colorVal: string,
    setColor: (v: string) => void,
    inputRef: React.RefObject<HTMLInputElement | null>,
    confirmLabel: string,
    onDelete?: () => void,
  ) => (
    <div className="tb-form">
      <input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        className="tb-form-name"
        value={nameVal}
        onChange={(e) => {
          setName(e.target.value);
          setFormError(null);
        }}
        onKeyDown={onFormKeyDown(onSave, onCancel)}
        placeholder="Trade name"
      />
      <ColorPicker
        value={colorVal}
        onChange={(c) => {
          setColor(c);
          setFormError(null);
        }}
        usedColors={usedColors}
      />
      <div className="tb-form-actions">
        <button className="tb-form-confirm" onClick={onSave} type="button">
          {confirmLabel}
        </button>
        <button className="tb-form-cancel" onClick={onCancel} type="button">
          Cancel
        </button>
        {onDelete && (
          <button className="tb-form-delete" onClick={onDelete} type="button">
            Delete
          </button>
        )}
      </div>
      {formError && <div className="tb-form-error">{formError}</div>}
    </div>
  );

  return (
    <div className="tb-panel">
      <div className="tb-panel-head">
        <div className="tb-panel-head-titles">
          <span className="tb-panel-title">Trades</span>
          <span className="tb-panel-hint">drag codes onto a trade to assign</span>
        </div>
        <button
          className="tb-panel-add"
          onClick={() => setAdding(true)}
          disabled={adding}
          type="button"
        >
          + Add Trade
        </button>
      </div>
      <div className="tb-panel-content">
        {adding && (
          <div ref={addFormRef}>
            {editForm(
              handleAdd,
              () => {
                setAdding(false);
                setFormError(null);
              },
              newName,
              setNewName,
              newColor,
              setNewColor,
              nameInputRef,
              "Add",
            )}
          </div>
        )}
        {/* Pinned unassign target — drop a code here to clear its trade. */}
        <div
          className={`tb-bucket tb-bucket--none ${
            unassignZone.dragOver ? "is-drag-over" : ""
          }`}
          onDragOver={unassignZone.handleDragOver}
          onDragLeave={unassignZone.handleDragLeave}
          onDrop={unassignZone.handleDrop}
          title="Drop a code here to remove its trade"
        >
          <div className="tb-bucket-head tb-bucket-head--static">
            <span className="tb-bucket-swatch tb-bucket-swatch--none" />
            <span className="tb-bucket-name">No trade</span>
            <span className="tb-bucket-count">({unassignedCount})</span>
          </div>
        </div>

        {trades.map((t) =>
          editingId === t.id
            ? (
              <React.Fragment key={t.id}>
                {editForm(
                  handleEditSave,
                  () => setEditingId(null),
                  editName,
                  setEditName,
                  editColor,
                  setEditColor,
                  editInputRef,
                  "Save",
                  () => {
                    setEditingId(null);
                    setConfirmDeleteId(t.id);
                  },
                )}
              </React.Fragment>
            )
            : (
              <TradeBucket
                key={t.id}
                trade={t}
                codes={codesByTrade.get(t.id) ?? []}
                expanded={expandedIds.has(t.id)}
                onToggleExpand={() => toggleExpand(t.id)}
                onAssign={onAssign}
                onStartEdit={startEdit}
                onRemove={onRemoveFromSection}
              />
            )
        )}
      </div>

      {confirmDeleteId != null && (
        <div className="tb-confirm-overlay" onClick={() => setConfirmDeleteId(null)}>
          <div className="tb-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <p>
              Delete trade{" "}
              <strong>{trades.find((t) => t.id === confirmDeleteId)?.name}</strong>?
            </p>
            <p className="tb-confirm-sub">
              Its codes become unassigned. Staged with your other edits — applies
              when you save (⌘Z undoes it).
            </p>
            <div className="tb-confirm-actions">
              <button
                className="tb-form-cancel"
                onClick={() => setConfirmDeleteId(null)}
                type="button"
              >
                Cancel
              </button>
              <button
                className="tb-confirm-delete"
                onClick={() => {
                  onDeleteTrade(confirmDeleteId);
                  setConfirmDeleteId(null);
                }}
                type="button"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradeBuckets;
